import { useMemo, useState } from 'react'
import { CheckCircle2, KeyRound } from 'lucide-react'
import type { DashboardUserProfile } from '@services/firebase'
import { PayPalCheckoutCard } from '@features/premium/components/PayPalCheckoutCard'
import {
  premiumBenefits,
  premiumPlans,
} from '@features/premium/shared/premium-plans'
import { LmsButton } from '@shared/ui'
import { PremiumLegacySurface } from './PremiumLegacySurface'

interface PremiumPurchasePanelProps {
  readonly onActivated: (profile: DashboardUserProfile) => void
  readonly onOpenActivationKey: () => void
  readonly profile: DashboardUserProfile
}

export function PremiumPurchasePanel({
  onActivated,
  onOpenActivationKey,
  profile,
}: PremiumPurchasePanelProps) {
  const [selectedPlanId, setSelectedPlanId] = useState(
    premiumPlans[1]?.id ?? premiumPlans[0].id,
  )
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null)

  const selectedPlan = useMemo(
    () =>
      premiumPlans.find((plan) => plan.id === selectedPlanId) ??
      premiumPlans[0],
    [selectedPlanId],
  )
  const checkoutPlan = useMemo(
    () =>
      premiumPlans.find((plan) => plan.id === checkoutPlanId) ?? null,
    [checkoutPlanId],
  )
  const savings = selectedPlan.originalPrice - selectedPlan.price

  return (
    <div className="premiumStack premiumStack--gap-3">
      <PremiumLegacySurface>
        <div className="premiumStack premiumStack--gap-3_5">
          <div className="premiumCenteredText">
            <h2 className="premiumSectionTitle premiumTypography premiumTypography--h4">
              Choose your plan
            </h2>
          </div>

          <div className="premiumPlanSelectorWrap">
            <div
              aria-label="Premium duration"
              className="premiumPlanSelector"
              role="group"
            >
              {premiumPlans.map((plan) => {
                const isSelected = plan.id === selectedPlan.id

                return (
                  <LmsButton
                    aria-pressed={isSelected}
                    className="premiumPlanSelectorButton"
                    key={plan.id}
                    onClick={() => {
                      setSelectedPlanId(plan.id)
                      setCheckoutPlanId(null)
                    }}
                    variant={isSelected ? 'secondary' : 'ghost'}
                  >
                    {plan.durationMonths} months
                  </LmsButton>
                )
              })}
            </div>
          </div>

          <div className="premiumPlanCardWrap">
            <div
              className={`premiumPlanCard ${selectedPlan.recommended ? 'isRecommended' : ''}`}
            >
              <div className="premiumStack premiumStack--gap-2_5">
                <div className="premiumStack premiumStack--gap-1">
                  {selectedPlan.recommended ? (
                    <span className="premiumRecommendationBadge">
                      Best value
                    </span>
                  ) : null}
                  <h3 className="premiumPlanTitle premiumTypography premiumTypography--h5">
                    {selectedPlan.label}
                  </h3>
                  <div className="premiumPriceRow">
                    <span className="premiumOriginalPrice premiumTypography premiumTypography--h6">
                      ${selectedPlan.originalMonthlyPrice.toFixed(1)}
                    </span>
                    <span className="premiumMonthlyPrice premiumTypography premiumTypography--h3">
                      ${selectedPlan.monthlyPrice.toFixed(1)}
                    </span>
                    <span className="premiumBillingSuffix premiumTypography premiumTypography--body2">
                      / month
                    </span>
                  </div>
                  <p className="premiumBillingNote premiumTypography premiumTypography--body2">
                    Billed every {selectedPlan.durationMonths} months for ${selectedPlan.price}
                  </p>
                </div>

                <div className="premiumBenefitList">
                  {premiumBenefits.map((benefit) => (
                    <div className="premiumBenefitItem" key={benefit}>
                      <CheckCircle2
                        aria-hidden="true"
                        className="premiumBenefitIcon"
                        size={16}
                        strokeWidth={2.5}
                      />
                      <span className="premiumTypography premiumTypography--body2">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>

                <LmsButton
                  fullWidth
                  onClick={() => {
                    setCheckoutPlanId(selectedPlan.id)
                  }}
                  variant="primary"
                >
                  Choose package
                </LmsButton>
              </div>
            </div>
          </div>
        </div>
      </PremiumLegacySurface>

      <div
        className={`premiumCheckoutGrid ${checkoutPlan ? 'hasCheckout' : ''}`}
      >
        <PremiumLegacySurface>
          <div className="premiumStack premiumStack--gap-2_5">
            <div className="premiumStack premiumStack--gap-1">
              <h3 className="premiumSectionTitle premiumTypography premiumTypography--h5">
                Order summary
              </h3>
              <p className="premiumSectionDescription premiumTypography premiumTypography--body2">
                Total: ${selectedPlan.price} for {selectedPlan.durationMonths} months
                of premium access.
              </p>
            </div>

            <div className="premiumStack premiumStack--gap-1">
              <p className="premiumSavingsNote premiumTypography premiumTypography--body1">
                Save ${savings} compared with the original package total.
              </p>
            </div>

            <div className="premiumActionRow premiumActionRow--gap-1_5">
              <LmsButton
                leadingIcon={<KeyRound aria-hidden="true" />}
                mobileFullWidth
                onClick={onOpenActivationKey}
                variant="outline"
              >
                I already have an activation key
              </LmsButton>
            </div>
          </div>
        </PremiumLegacySurface>

        {checkoutPlan ? (
          <PayPalCheckoutCard
            onActivated={onActivated}
            plan={checkoutPlan}
            profile={profile}
          />
        ) : null}
      </div>
    </div>
  )
}
